// frontend\src\hooks\useTableSort.tsx
// Generic client-side column sorting for tables. Pair with <SortableTh>.
import { useMemo, useState } from 'react';

export type SortDir = 'asc' | 'desc';

export interface TableSort<T> {
  sorted: T[];
  sortKey: keyof T;
  sortDir: SortDir;
  toggleSort: (key: keyof T) => void;
}

/**
 * Per-column comparators replacing the default value comparison for that column.
 * Receives whole rows, so a column can rank on more than its own value (e.g.
 * grouping out-of-stock rows and ordering them by sales velocity). Return the
 * ascending order; the hook flips it for descending. Nulls in the sorted column
 * still go last, before the comparator is consulted.
 * Define these outside the component (or memoize) so the sort isn't redone every render.
 */
export type SortComparators<T> = Partial<Record<keyof T, (a: T, b: T) => number>>;

export function useTableSort<T>(
  data: T[],
  initialKey: keyof T,
  initialDir: SortDir = 'asc',
  comparators?: SortComparators<T>
): TableSort<T> {
  const [sortKey, setSortKey] = useState<keyof T>(initialKey);
  const [sortDir, setSortDir] = useState<SortDir>(initialDir);

  const sorted = useMemo(() => {
    const copy = [...data];
    const custom = comparators?.[sortKey];
    copy.sort((a, b) => {
      const av = a[sortKey] as unknown;
      const bv = b[sortKey] as unknown;
      // Nulls always sort last regardless of direction
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (custom) {
        const c = custom(a, b);
        return sortDir === 'asc' ? c : -c;
      }
      let cmp = 0;
      if (typeof av === 'number' && typeof bv === 'number') {
        cmp = av - bv;
      } else if (typeof av === 'boolean' && typeof bv === 'boolean') {
        cmp = av === bv ? 0 : av ? -1 : 1;
      } else {
        cmp = String(av).toLowerCase().localeCompare(String(bv).toLowerCase());
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [data, sortKey, sortDir, comparators]);

  const toggleSort = (key: keyof T) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  return { sorted, sortKey, sortDir, toggleSort };
}

interface SortableThProps<T> {
  label: string;
  column: keyof T;
  sort: TableSort<T>;
  center?: boolean;
  className?: string;
}

export function SortableTh<T>({ label, column, sort, center, className }: SortableThProps<T>) {
  return (
    <th
      className={`dash-th-sortable ${className || ''}`}
      style={center ? { textAlign: 'center' } : undefined}
      onClick={() => sort.toggleSort(column)}
    >
      {label}
      {sort.sortKey === column && <span className="dash-sort-arrow">{sort.sortDir === 'asc' ? ' ↑' : ' ↓'}</span>}
    </th>
  );
}
